import { Component, DestroyRef, ElementRef, Inject, OnInit, signal, ViewChild } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmModalComponent, ConfirmModalData, PopOverService, SnackbarComponent, SnackbarData } from '@entscheidungsnavi/widgets';
import { OnlineProjectsService, ProjectDto, ProjectFoldersService, QuickstartProjectDto } from '@entscheidungsnavi/api-client';
import { concat, concatMap, firstValueFrom, forkJoin, ignoreElements, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectFolderDto, TeamProjectInfoWithRoleDto } from '@entscheidungsnavi/api-types';
import { saveErrorToMessage } from '../../../data/project/save-notification';
import { OnlineProjectManagementService } from '../project-management.service';
import { ProjectService } from '../../../data/project';

export type SaveAsModalData = {
  projects: readonly ProjectDto[];
  projectFolders: readonly ProjectFolderDto[];
} | null;

export type SaveAsModalResult = {
  projects: readonly ProjectDto[];
  projectFolders: readonly ProjectFolderDto[];
};

@Component({
  templateUrl: './save-as-modal.component.html',
  styleUrls: ['./save-as-modal.component.scss'],
  standalone: false,
})
export class SaveAsModalComponent implements OnInit {
  @ViewChild('saveButton', { read: ElementRef }) private saveButton: ElementRef<HTMLElement>;

  readonly newProjectForm = this.fb.group({
    name: [this.projectService.getProjectName(), Validators.required],
  });

  protected projects: readonly ProjectDto[];
  protected projectFolders: readonly ProjectFolderDto[];

  protected currentFolderId = signal<string | null>(null);

  protected highlightedProjectName = signal<string | null>(null);

  get existingProject() {
    return this.projects.find(project => project.name.toLowerCase() === this.newProjectForm.value.name.toLowerCase());
  }

  state: 'loading' | 'load-error' | 'idle' | 'saving' = 'loading';

  constructor(
    private dialogRef: MatDialogRef<SaveAsModalComponent, SaveAsModalResult>,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private managementService: OnlineProjectManagementService,
    onlineProjectsService: OnlineProjectsService,
    private popoverService: PopOverService,
    private fb: NonNullableFormBuilder,
    @Inject(MAT_DIALOG_DATA) data: SaveAsModalData,
    private projectService: ProjectService,
    private destroyRef: DestroyRef,
    private projectFoldersService: ProjectFoldersService,
  ) {
    (data
      ? of(data)
      : forkJoin({ projects: onlineProjectsService.getProjectList(), projectFolders: projectFoldersService.getProjectFolders() })
    ).subscribe({
      next: ({ projects, projectFolders }) => {
        this.projects = projects;
        this.projectFolders = projectFolders;
        this.state = 'idle';
      },
      error: () => (this.state = 'load-error'),
    });
  }

  ngOnInit() {
    this.newProjectForm.markAllAsTouched();
  }

  closeModal() {
    this.dialogRef.close();
  }

  clickedProject(project: QuickstartProjectDto | ProjectDto | TeamProjectInfoWithRoleDto) {
    this.newProjectForm.controls.name.setValue(project.name);

    const containingFolder = this.projectFolders.find(folder => folder.children.includes(project.id));

    this.currentFolderId.set(containingFolder?.id);
  }

  async saveAs() {
    if (this.state !== 'idle' || this.newProjectForm.invalid) {
      return;
    }

    const newName = this.newProjectForm.value.name;

    const existingProject = this.existingProject;

    if (existingProject) {
      const hierarchy = this.projectFoldersService.generateProjectHierarchy(this.projectFolders, this.projects);

      const folder = hierarchy.allFolders.find(folder => folder.children.some(child => child.id === existingProject.id));

      let folderPath = '';

      if (folder) {
        let currentFolder = folder;

        while (currentFolder.parent) {
          folderPath = `${currentFolder.name}${folderPath ? '/' + folderPath : ''}`;
          currentFolder = currentFolder.parent;
        }
      }

      const prompt = $localize`
      Es existiert bereits ein Projekt mit dem Namen „${existingProject.name}“${folderPath ? ' im Ordner „' + folderPath + '“' : ''}.
        Bist du sicher, dass du dieses Projekt überschreiben möchtest?`;

      const confirmed = await firstValueFrom(
        this.dialog
          .open<ConfirmModalComponent, ConfirmModalData>(ConfirmModalComponent, {
            data: {
              prompt,
              title: $localize`Projekt überschreiben`,
              buttonConfirm: $localize`Ja, überschreiben`,
            },
          })
          .afterClosed(),
      );

      if (!confirmed) {
        return;
      }
    }

    this.state = 'saving';

    let saveObservable = existingProject
      ? this.managementService.saveAsExistingProject(existingProject.id)
      : this.managementService.saveAsNewProject(newName);

    if (this.currentFolderId() != null) {
      saveObservable = saveObservable.pipe(
        concatMap(project =>
          concat(this.projectFoldersService.moveIntoFolder(project.info.id, this.currentFolderId()).pipe(ignoreElements()), of(project)),
        ),
      );
    }

    saveObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: project => {
        this.projectFolders.forEach(folder => {
          folder.children = folder.children.filter(id => id !== project.dto.id);

          if (folder.id === this.currentFolderId()) {
            folder.children.push(project.info.id);
          }
        });

        this.projects = [...this.projects.filter(p => p.id !== project.dto.id), project.dto];

        this.dialogRef.close({
          projects: this.projects,
          projectFolders: this.projectFolders,
        });
        this.snackBar.openFromComponent<SnackbarComponent, SnackbarData>(SnackbarComponent, {
          data: { message: $localize`Erfolgreich gespeichert`, icon: 'done' },
          duration: 2500,
        });
      },
      error: error => {
        this.state = 'idle';
        this.popoverService.whistle(this.saveButton, saveErrorToMessage(error), 'error', 5000);
      },
    });
  }
}
